import { motion } from "framer-motion";
import { useLang } from "../i18n/LanguageContext";
import { SALON_PHOTOS } from "../data/assets";

const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

export const About = () => {
  const { t } = useLang();

  return (
    <section
      id="about"
      className="relative py-24 md:py-36 bg-[#050505] overflow-hidden"
      data-testid="about-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-100px" }}
            variants={reveal}
            className="lg:col-span-6 relative"
          >
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src={SALON_PHOTOS.about}
                alt="Luxury Salón interiér"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/40 to-transparent" />
            </div>
            {/* Gold accent frame */}
            <div className="hidden md:block absolute -bottom-6 -right-6 w-2/3 h-2/3 border border-[#C5A059]/30 -z-0 pointer-events-none" />

            {/* Floating badge */}
            <div className="absolute top-6 left-6 bg-[#050505]/80 backdrop-blur-md border border-white/10 px-5 py-3">
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/50">
                Žilina
              </div>
              <div className="font-display text-white text-lg">Luxury Salón</div>
            </div>
          </motion.div>

          {/* Text */}
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-100px" }}
            variants={{
              hidden: { opacity: 0 },
              show: { opacity: 1, transition: { staggerChildren: 0.15 } },
            }}
            className="lg:col-span-6"
          >
            <motion.div variants={reveal} className="flex items-center gap-4 mb-6">
              <span className="h-px w-10 bg-[#C5A059]" />
              <span className="text-xs uppercase tracking-[0.3em] text-[#C5A059]">
                {t.about.eyebrow}
              </span>
            </motion.div>

            <motion.h2
              variants={reveal}
              className="font-display text-4xl md:text-5xl lg:text-6xl text-white leading-[1.05] tracking-tight"
              data-testid="about-title"
            >
              {t.about.title}
            </motion.h2>

            <motion.p
              variants={reveal}
              className="mt-8 text-base md:text-lg text-white/65 font-light leading-[1.85] max-w-xl"
              data-testid="about-body"
            >
              {t.about.body}
            </motion.p>

            {/* Stats */}
            <motion.div
              variants={reveal}
              className="mt-12 grid grid-cols-3 gap-6 pt-10 border-t border-white/10"
            >
              {[
                { v: t.about.stat1_value, l: t.about.stat1_label },
                { v: t.about.stat2_value, l: t.about.stat2_label },
                { v: t.about.stat3_value, l: t.about.stat3_label },
              ].map((s, i) => (
                <div key={i} data-testid={`about-stat-${i}`}>
                  <div className="font-display text-2xl md:text-4xl text-[#C5A059] mb-2">
                    {s.v}
                  </div>
                  <div className="text-[10px] md:text-xs uppercase tracking-[0.2em] text-white/50">
                    {s.l}
                  </div>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
